import React, { useState, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useTransactionPalette } from '@/hooks/admin/useTransactionPalette';
import { GlassDataTable } from '@/components/admin/redesign';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { IAdminTransaction, TransactionSortField, TransactionSortOrder } from '@/types/admin/transactions';
import {
  getPaymentStatusColor,
  getTransactionAmountColor,
  getPayerName,
  getTransactionTypeLabel,
  isPayment,
  isCharge,
} from '@/utils/admin/transactions';

type TransactionFilterType = 'all' | 'charges' | 'payments';

interface TransactionTableProps {
  transactions: IAdminTransaction[];
  maxRows?: number;
  showPager?: boolean;
  showFilter?: boolean;
}

export const TransactionTable: React.FC<TransactionTableProps> = ({
  transactions,
  maxRows = 10,
  showPager = false,
  showFilter = true,
}) => {
  const [sortField, setSortField] = useState<TransactionSortField>('date');
  const [sortOrder, setSortOrder] = useState<TransactionSortOrder>('desc');
  const [filterType, setFilterType] = useState<TransactionFilterType>('all');
  const styles = useStyles();
  const { admin, redesign, semantic } = useAdminColors();
  const transactionPalette = useTransactionPalette();

  const statusPalette = useMemo(
    () => ({
      success: semantic.status.success,
      error: semantic.status.error,
      pending: semantic.status.warning,
      voided: semantic.text.muted,
      fallback: redesign.text.dim,
    }),
    [semantic.status.success, semantic.status.error, semantic.status.warning, semantic.text.muted, redesign.text.dim],
  );

  const amountPalette = useMemo(
    () => ({ payment: transactionPalette.payment, charge: transactionPalette.charge }),
    [transactionPalette.payment, transactionPalette.charge],
  );

  const filteredAndSortedTransactions = useMemo(() => {
    let filtered = transactions;
    if (filterType === 'charges') {
      filtered = transactions.filter(isCharge);
    } else if (filterType === 'payments') {
      filtered = transactions.filter(isPayment);
    }

    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'date':
          comparison = new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime();
          break;
        case 'amount':
          comparison = Math.abs(a.amount) - Math.abs(b.amount);
          break;
        case 'description':
          comparison = (a.description || '').localeCompare(b.description || '');
          break;
        case 'type':
          comparison = (a.type || '').localeCompare(b.type || '');
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return maxRows ? sorted.slice(0, maxRows) : sorted;
  }, [transactions, sortField, sortOrder, maxRows, filterType]);

  const handleSort = (field: TransactionSortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIndicator = ({ field }: { field: TransactionSortField }) => {
    if (sortField !== field) return null;
    return (
      <Text style={[styles.sortIndicator, { color: admin.dataCell.textAccent }]}>
        {sortOrder === 'asc' ? ' ↑' : ' ↓'}
      </Text>
    );
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    const color = getPaymentStatusColor(status, statusPalette);
    return (
      <View style={[styles.statusBadge, { backgroundColor: color + '20' }]}>
        <Text style={[styles.statusText, { color }]}>{status}</Text>
      </View>
    );
  };

  const getTypeBadge = (transaction: IAdminTransaction) => {
    const label = getTransactionTypeLabel(transaction);
    const accent = isPayment(transaction)
      ? transactionPalette.payment
      : transactionPalette.charge;

    return (
      <View style={[styles.typeBadge, { backgroundColor: accent + '15' }]}>
        <Text style={[styles.typeText, { color: accent }]}>{label}</Text>
      </View>
    );
  };

  const FilterButton = ({ type, label }: { type: TransactionFilterType; label: string }) => {
    const isActive = filterType === type;
    return (
      <TouchableOpacity
        style={[styles.filterButton, isActive && styles.filterButtonActive]}
        onPress={() => setFilterType(type)}
      >
        <Text style={[styles.filterButtonText, isActive && styles.filterButtonTextActive]}>
          {label}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderRow = ({ item }: { item: IAdminTransaction }) => {
    const amountColor = getTransactionAmountColor(item, amountPalette);
    return (
      <View style={styles.tableRow}>
        <View style={[styles.cell, styles.dateCell]}>
          <Text style={styles.cellText}>{formatShortDate(item.transactionDate)}</Text>
        </View>
        <View style={[styles.cell, styles.typeCell]}>{getTypeBadge(item)}</View>
        <View style={[styles.cell, styles.descriptionCell]}>
          <Text style={styles.cellText} numberOfLines={2}>
            {item.description}
            {item.arCodeDescription && (
              <Text style={styles.subText}>{'\n'}{item.arCodeDescription}</Text>
            )}
          </Text>
        </View>
        <View style={[styles.cell, styles.amountCell]}>
          <Text
            style={[
              styles.cellText,
              styles.cellTextRight,
              { color: amountColor, fontWeight: '600' },
            ]}
          >
            {formatCurrency(Math.abs(item.amount))}
          </Text>
        </View>
        <View style={[styles.cell, styles.statusCell]}>
          {getStatusBadge(item.paymentSettlementStatus)}
        </View>
        <View style={[styles.cell, styles.payerCell]}>
          <Text style={styles.cellText} numberOfLines={1}>{getPayerName(item)}</Text>
        </View>
      </View>
    );
  };

  const headerContent = (
    <View style={styles.tableHeader}>
      <TouchableOpacity style={[styles.headerCell, styles.dateCell]} onPress={() => handleSort('date')}>
        <Text style={styles.headerText}>DATE<SortIndicator field="date" /></Text>
      </TouchableOpacity>
      <TouchableOpacity style={[styles.headerCell, styles.typeCell]} onPress={() => handleSort('type')}>
        <Text style={styles.headerText}>TYPE<SortIndicator field="type" /></Text>
      </TouchableOpacity>
      <TouchableOpacity style={[styles.headerCell, styles.descriptionCell]} onPress={() => handleSort('description')}>
        <Text style={styles.headerText}>DESCRIPTION<SortIndicator field="description" /></Text>
      </TouchableOpacity>
      <TouchableOpacity style={[styles.headerCell, styles.amountCell]} onPress={() => handleSort('amount')}>
        <Text style={[styles.headerText, styles.headerTextRight]}>AMOUNT<SortIndicator field="amount" /></Text>
      </TouchableOpacity>
      <View style={[styles.headerCell, styles.statusCell]}>
        <Text style={styles.headerText}>STATUS</Text>
      </View>
      <View style={[styles.headerCell, styles.payerCell]}>
        <Text style={styles.headerText}>PAYER</Text>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {showFilter && (
        <View style={styles.filterContainer}>
          <FilterButton type="all" label="All Transactions" />
          <FilterButton type="charges" label="Charges Only" />
          <FilterButton type="payments" label="Payments Only" />
        </View>
      )}

      <GlassDataTable header={headerContent}>
        <FlatList
          data={filteredAndSortedTransactions}
          renderItem={renderRow}
          keyExtractor={(item) => String(item.transactionId)}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          showsVerticalScrollIndicator={false}
          style={styles.tableBody}
        />
      </GlassDataTable>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      container: {
        flex: 1,
      },
      filterContainer: {
        flexDirection: 'row',
        paddingHorizontal: 16,
        paddingVertical: 12,
        gap: 8,
        backgroundColor: 'transparent',
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
      },
      filterButton: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 6,
        backgroundColor: components.admin.dataTable.filterBg,
        borderWidth: 1,
        borderColor: components.admin.dataTable.border,
      },
      filterButtonActive: {
        backgroundColor: components.admin.selector.itemSelectedBg,
        borderColor: components.admin.selector.itemSelectedText,
      },
      filterButtonText: {
        fontSize: 13,
        fontWeight: '500',
        color: components.admin.dataCell.textMuted,
      },
      filterButtonTextActive: {
        color: components.admin.selector.itemSelectedText,
        fontWeight: '600',
      },
      tableHeader: {
        flexDirection: 'row',
        paddingVertical: 12,
        paddingHorizontal: 8,
      },
      headerCell: {
        paddingHorizontal: 8,
        justifyContent: 'center',
        alignItems: 'flex-start',
      },
      headerText: {
        fontSize: 11,
        fontWeight: '700',
        color: components.admin.dataCell.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      },
      headerTextRight: {
        textAlign: 'right',
        width: '100%',
      },
      sortIndicator: {
        fontSize: 10,
      },
      tableBody: {
        flex: 1,
      },
      tableRow: {
        flexDirection: 'row',
        paddingVertical: 12,
        paddingHorizontal: 8,
        alignItems: 'center',
      },
      cell: {
        paddingHorizontal: 8,
        justifyContent: 'center',
      },
      cellText: {
        fontSize: 13,
        color: colors.text.primary,
      },
      cellTextRight: {
        textAlign: 'right',
      },
      dateCell: {
        width: 100,
      },
      typeCell: {
        width: 90,
        alignItems: 'flex-start',
      },
      descriptionCell: {
        flex: 2,
        minWidth: 80,
      },
      amountCell: {
        width: 100,
      },
      statusCell: {
        width: 160,
        alignItems: 'flex-start',
      },
      payerCell: {
        width: 120,
      },
      subText: {
        fontSize: 11,
        color: components.admin.dataCell.textMuted,
      },
      statusBadge: {
        paddingHorizontal: 6,
        paddingVertical: 3,
        borderRadius: 3,
      },
      statusText: {
        fontSize: 10,
        fontWeight: '600',
      },
      typeBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        alignSelf: 'flex-start',
      },
      typeText: {
        fontSize: 11,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      },
      separator: {
        height: 1,
        backgroundColor: components.admin.dataTable.border,
        marginHorizontal: 8,
      },
    }),
  );
